import { FormEvent, useState } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/useToast';

export default function Login() {
  const { user, ready, login } = useAuth();
  const nav = useNavigate();
  const { push } = useToast();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [totp, setTotp] = useState('');
  const [needTotp, setNeedTotp] = useState(false);
  const [busy, setBusy] = useState(false);

  if (ready && user) return <Navigate to="/" replace />;

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    try {
      await login(email, password, totp || undefined);
      setEmail('');
      setPassword('');
      nav('/');
    } catch (err: any) {
      if (err?.code === 'UNAUTHORIZED' && err?.message?.toLowerCase().includes('totp')) {
        setNeedTotp(true);
      }
      push({ tone: 'error', title: 'Sign in failed', body: err?.message ?? 'Unknown error' });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-screen grid place-items-center bg-gradient-to-br from-slate-50 to-rdx-50">
      <form onSubmit={onSubmit} className="card card-pad w-full max-w-[400px] mx-4 space-y-4">
        <div className="flex items-center gap-2 mb-2">
          <div className="w-9 h-9 rounded bg-rdx text-white font-bold grid place-items-center">R</div>
          <div>
            <div className="font-semibold">RDX PI Generator</div>
            <div className="text-xs text-muted">Sign in to continue</div>
          </div>
        </div>

        <div>
          <label className="label">Email</label>
          <input
            className="input mt-1"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoFocus
            required
            autoComplete="email"
          />
        </div>
        <div>
          <label className="label">Password</label>
          <input
            className="input mt-1"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            autoComplete="new-password"
          />
        </div>
        {needTotp && (
          <div>
            <label className="label">Authenticator code</label>
            <input
              className="input mt-1 tracking-widest font-mono text-center"
              inputMode="numeric"
              maxLength={6}
              value={totp}
              onChange={(e) => setTotp(e.target.value)}
            />
          </div>
        )}
        <button type="submit" disabled={busy} className="btn-primary w-full btn-lg">
          {busy ? 'Signing in…' : 'Sign in'}
        </button>
        {import.meta.env.DEV && (
          <div className="text-[11px] text-muted text-center pt-1">
            Seeded admin: admin@rdx.local / ChangeMe!Admin1
          </div>
        )}
      </form>
    </div>
  );
}
